"use client";

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
} from "react";
import { createClient } from "@/lib/supabase/client";
import { ROLES, getRoleName, type RoleId } from "@/lib/constants/roles";
import { STATUS_IDS } from "@/lib/constants/statuses";

// Types
export interface User {
  id: string;
  email: string;
  name?: string;
  role_id: RoleId;
  role: string; // Keep for backward compatibility, will be derived from role_id
  venue_id?: number;
  status: string;
}

export interface Venue {
  id: number;
  name: string;
  currency: string;
  address?: string;
}

export interface GlobalState {
  // Auth state
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Venue state
  selectedVenue: Venue | null;
  availableVenues: Venue[];
  venuePermissions: number[];

  // UI state
  sidebarOpen: boolean;

  // Error state
  error: string | null;
}

export interface GlobalActions {
  // Auth actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;

  // Venue actions
  setSelectedVenue: (venue: Venue | null) => void;
  setAvailableVenues: (venues: Venue[]) => void;
  setVenuePermissions: (permissions: number[]) => void;

  // Role helper functions
  isSuperAdmin: () => boolean;
  isBookingStaff: () => boolean;
  isBarStaff: () => boolean;
  hasRole: (roleId: RoleId) => boolean;

  // UI actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Error actions
  setError: (error: string | null) => void;

  // Combined actions
  refreshUserData: () => Promise<void>;
  logout: () => Promise<void>;
}

// Action types
type ActionType =
  | { type: "SET_USER"; payload: User | null }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_SELECTED_VENUE"; payload: Venue | null }
  | { type: "SET_AVAILABLE_VENUES"; payload: Venue[] }
  | { type: "SET_VENUE_PERMISSIONS"; payload: number[] }
  | { type: "TOGGLE_SIDEBAR" }
  | { type: "SET_SIDEBAR_OPEN"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "RESET_STATE" };

// Initial state
const initialState: GlobalState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  selectedVenue: null,
  availableVenues: [],
  venuePermissions: [],
  sidebarOpen: false,
  error: null,
};

// Reducer
function globalStateReducer(
  state: GlobalState,
  action: ActionType,
): GlobalState {
  switch (action.type) {
    case "SET_USER":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    case "SET_SELECTED_VENUE":
      return {
        ...state,
        selectedVenue: action.payload,
      };
    case "SET_AVAILABLE_VENUES":
      return {
        ...state,
        availableVenues: action.payload,
      };
    case "SET_VENUE_PERMISSIONS":
      return {
        ...state,
        venuePermissions: action.payload,
      };
    case "TOGGLE_SIDEBAR":
      return {
        ...state,
        sidebarOpen: !state.sidebarOpen,
      };
    case "SET_SIDEBAR_OPEN":
      return {
        ...state,
        sidebarOpen: action.payload,
      };
    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
      };
    case "RESET_STATE":
      return {
        ...initialState,
        isLoading: false,
      };
    default:
      return state;
  }
}

// Context
const AuthAndPermissionsContext = createContext<
  (GlobalState & GlobalActions) | undefined
>(undefined);

// Provider component
export function AuthAndPermissionsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(globalStateReducer, initialState);
  const supabase = createClient();

  // Local storage keys
  const STORAGE_KEYS = {
    selectedVenue: "onair_selected_venue",
    sidebarOpen: "onair_sidebar_open",
  };

  // Load persisted state from localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        // Load sidebar state
        const savedSidebarState = localStorage.getItem(
          STORAGE_KEYS.sidebarOpen,
        );
        if (savedSidebarState !== null) {
          dispatch({
            type: "SET_SIDEBAR_OPEN",
            payload: JSON.parse(savedSidebarState),
          });
        }

        // Load selected venue
        const savedVenue = localStorage.getItem(STORAGE_KEYS.selectedVenue);
        if (savedVenue) {
          dispatch({
            type: "SET_SELECTED_VENUE",
            payload: JSON.parse(savedVenue),
          });
        }
      } catch (error) {
        console.error("Error loading state from localStorage:", error);
      }
    }
  }, []);

  // Persist state to localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(
          STORAGE_KEYS.sidebarOpen,
          JSON.stringify(state.sidebarOpen),
        );
      } catch (error) {
        console.error("Error saving sidebar state to localStorage:", error);
      }
    }
  }, [state.sidebarOpen]);

  useEffect(() => {
    if (typeof window !== "undefined" && state.selectedVenue) {
      try {
        localStorage.setItem(
          STORAGE_KEYS.selectedVenue,
          JSON.stringify(state.selectedVenue),
        );
      } catch (error) {
        console.error("Error saving venue state to localStorage:", error);
      }
    }
  }, [state.selectedVenue]);

  // Initialize auth state
  useEffect(() => {
    // Check if we're on a public page and skip initial auth check
    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : "";
    const publicRoutes = ["/login", "/forgot-password", "/login-otp"];
    const isPublicRoute = publicRoutes.includes(currentPath);

    if (!isPublicRoute) {
      refreshUserData();
    } else {
      // For public routes, just check if user is already logged in
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user) {
          refreshUserData();
        } else {
          dispatch({ type: "SET_LOADING", payload: false });
        }
      });
    }

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        dispatch({ type: "RESET_STATE" });
        // Clear localStorage on logout
        if (typeof window !== "undefined") {
          localStorage.removeItem(STORAGE_KEYS.selectedVenue);
        }
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        // Skip refresh for anonymous users - they have no users_infos record
        // and refreshing would set loading state, killing any in-flight booking submission
        if (session.user?.is_anonymous) {
          return;
        }
        await refreshUserData();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Actions
  const refreshUserData = async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      // Try to refresh session before getting user
      await supabase.auth.refreshSession();

      const {
        data: { user: authUser },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !authUser) {
        dispatch({ type: "SET_USER", payload: null });
        return;
      }

      // Get user info with role and venue details
      const { data: userInfo, error: userError } = await supabase
        .from("users_infos")
        .select(
          `
          *,
          roles(name),
          venues:venue_id(id, currency, venue_infos(address, lang)),
          statuses(name)
        `,
        )
        .eq("user_id", authUser.id)
        .single();

      if (userError || !userInfo) {
        dispatch({
          type: "SET_ERROR",
          payload: "Failed to load user information",
        });
        dispatch({ type: "SET_USER", payload: null });
        return;
      }

      // Check if user account is active
      if (userInfo.status_id !== STATUS_IDS.USER_ACTIVE) {
        // Sign out the user
        await supabase.auth.signOut();
        dispatch({ type: "SET_USER", payload: null });
        dispatch({
          type: "SET_ERROR",
          payload: "Your account has been deactivated",
        });
        return;
      }

      // Set user data
      const user: User = {
        id: authUser.id,
        email: authUser.email || "",
        name: userInfo.name || "",
        role_id: userInfo.role_id || 0,
        role: getRoleName(userInfo.role_id || 0), // Derive role name from role_id
        venue_id: userInfo.venue_id,
        status: userInfo.statuses?.name || "",
      };

      dispatch({ type: "SET_USER", payload: user });

      // Fetch available venues and permissions
      await fetchVenuesAndPermissions(user, userInfo.id);
    } catch (error) {
      console.error("Error refreshing user data:", error);
      dispatch({ type: "SET_ERROR", payload: "Failed to load user data" });
      dispatch({ type: "SET_USER", payload: null });
    }
  };

  const fetchVenuesAndPermissions = async (user: User, userInfoId: number) => {
    try {
      // Fetch venues based on user role
      if (user.role === "super_admin") {
        // Super admins can access all venues
        const { data: allVenues } = await supabase
          .from("venues")
          .select(
            `
            id, 
            currency, 
            venue_infos!inner(name, address, lang)
          `,
          )
          .eq("venue_infos.lang", "en")
          .order("id"); // Order by venue id instead of joined table field

        const venues =
          allVenues
            ?.map((v) => ({
              id: v.id,
              name: v.venue_infos?.[0]?.name || "Unnamed Venue",
              currency: v.currency,
              address: v.venue_infos?.[0]?.address || "",
            }))
            .sort((a, b) => a.name.localeCompare(b.name)) || [];
        dispatch({ type: "SET_AVAILABLE_VENUES", payload: venues });
        dispatch({
          type: "SET_VENUE_PERMISSIONS",
          payload: venues.map((v) => v.id),
        });
      } else {
        // Other users see only their permitted venues
        const { data: userVenues } = await supabase
          .from("user_venue_permissions")
          .select(
            `
            venue_id,
            venues:venue_id(
              id, 
              currency,
              venue_infos!inner(name, address, lang)
            )
          `,
          )
          .eq("user_id", userInfoId)
          .eq("venues.venue_infos.lang", "en");

        const venues = (userVenues
          ?.map((uv) => {
            const venue = Array.isArray(uv.venues) ? uv.venues[0] : uv.venues;
            if (!venue) return null;
            return {
              id: venue.id,
              name: venue.venue_infos?.[0]?.name || "Unnamed Venue",
              currency: venue.currency,
              address: venue.venue_infos?.[0]?.address || "",
            };
          })
          .filter((v): v is NonNullable<typeof v> => v !== null) ||
          []) as Venue[];

        // Include primary venue if set
        if (user.venue_id) {
          const { data: primaryVenue } = await supabase
            .from("venues")
            .select(
              `
              id, 
              currency,
              venue_infos!inner(name, address, lang)
            `,
            )
            .eq("id", user.venue_id)
            .eq("venue_infos.lang", "en")
            .single();

          if (primaryVenue && !venues.find((v) => v.id === primaryVenue.id)) {
            venues.push({
              id: primaryVenue.id,
              name: primaryVenue.venue_infos?.[0]?.name || "Unnamed Venue",
              currency: primaryVenue.currency,
              address: primaryVenue.venue_infos?.[0]?.address || "",
            });
          }
        }

        dispatch({ type: "SET_AVAILABLE_VENUES", payload: venues });
        dispatch({
          type: "SET_VENUE_PERMISSIONS",
          payload: venues.map((v) => v.id),
        });

        // Auto-select primary venue if no venue is selected
        if (!state.selectedVenue && venues.length > 0) {
          const defaultVenue =
            venues.find((v) => v.id === user.venue_id) || venues[0];
          dispatch({ type: "SET_SELECTED_VENUE", payload: defaultVenue });
        }
      }
    } catch (error) {
      console.error("Error fetching venues:", error);
      dispatch({ type: "SET_ERROR", payload: "Failed to load venue data" });
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      dispatch({ type: "RESET_STATE" });
      // Clear localStorage
      if (typeof window !== "undefined") {
        localStorage.removeItem(STORAGE_KEYS.selectedVenue);
        // Redirect to login page
        window.location.href = "/login";
      }
    } catch (error) {
      console.error("Error signing out:", error);
      dispatch({ type: "SET_ERROR", payload: "Failed to sign out" });
    }
  };

  // Role helper functions
  const isSuperAdmin = () => state.user?.role_id === ROLES.SUPER_ADMIN;
  const isBookingStaff = () => state.user?.role_id === ROLES.BOOKING_STAFF;
  const isBarStaff = () => state.user?.role_id === ROLES.BAR_STAFF;
  const hasRole = (roleId: RoleId) => state.user?.role_id === roleId;

  // Action creators
  const actions: GlobalActions = {
    setUser: (user) => dispatch({ type: "SET_USER", payload: user }),
    setLoading: (loading) =>
      dispatch({ type: "SET_LOADING", payload: loading }),
    setSelectedVenue: (venue) =>
      dispatch({ type: "SET_SELECTED_VENUE", payload: venue }),
    setAvailableVenues: (venues) =>
      dispatch({ type: "SET_AVAILABLE_VENUES", payload: venues }),
    setVenuePermissions: (permissions) =>
      dispatch({ type: "SET_VENUE_PERMISSIONS", payload: permissions }),
    toggleSidebar: () => dispatch({ type: "TOGGLE_SIDEBAR" }),
    setSidebarOpen: (open) =>
      dispatch({ type: "SET_SIDEBAR_OPEN", payload: open }),
    setError: (error) => dispatch({ type: "SET_ERROR", payload: error }),
    refreshUserData,
    logout,
    // Role helper functions
    isSuperAdmin,
    isBookingStaff,
    isBarStaff,
    hasRole,
  };

  const contextValue = {
    ...state,
    ...actions,
  };

  return (
    <AuthAndPermissionsContext.Provider value={contextValue}>
      {children}
    </AuthAndPermissionsContext.Provider>
  );
}

// Hook to use the context
export function useAuthAndPermissions() {
  const context = useContext(AuthAndPermissionsContext);
  if (context === undefined) {
    throw new Error(
      "useAuthAndPermissions must be used within an AuthAndPermissionsProvider",
    );
  }
  return context;
}

// Convenience hooks for specific parts of the state
export function useAuth() {
  const context = useAuthAndPermissions();
  return {
    user: context.user,
    isAuthenticated: context.isAuthenticated,
    isLoading: context.isLoading,
    logout: context.logout,
    refreshUserData: context.refreshUserData,
  };
}

export function useVenues() {
  const context = useAuthAndPermissions();
  return {
    selectedVenue: context.selectedVenue,
    availableVenues: context.availableVenues,
    venuePermissions: context.venuePermissions,
    setSelectedVenue: context.setSelectedVenue,
    setAvailableVenues: context.setAvailableVenues,
  };
}

export function useUI() {
  const context = useAuthAndPermissions();
  return {
    sidebarOpen: context.sidebarOpen,
    toggleSidebar: context.toggleSidebar,
    setSidebarOpen: context.setSidebarOpen,
  };
}

// CORE-004 spec hooks
export function useCurrentUser() {
  const context = useAuthAndPermissions();
  if (!context.user && !context.isLoading) {
    throw new Error("User is required but not authenticated");
  }
  return context.user;
}

export function useSelectedVenue() {
  const context = useAuthAndPermissions();
  // Return venue ID or 'global' for super admin global context
  if (!context.selectedVenue) {
    return context.isSuperAdmin() ? "global" : null;
  }
  return context.selectedVenue.id.toString();
}

export function useUserPermissions() {
  const context = useAuthAndPermissions();
  return {
    venues: context.availableVenues,
    venueIds: context.venuePermissions,
    hasVenueAccess: (venueId: number) =>
      context.venuePermissions.includes(venueId),
  };
}
